import React from 'react';
import {isSafari} from 'react-device-detect';
import {google, ics, office365, outlook, yahoo} from 'calendar-link';
import {Appointment} from 'components/ContentPagesComponents/TextHeader/query';

import Apple from './svgs/apple.inline.svg';
import Google from './svgs/google.inline.svg';
import Microsoft365 from './svgs/microsoft365.inline.svg';
import Outlook from './svgs/outlook.inline.svg';
import Yahoo from './svgs/yahoo.inline.svg';

type Calendar =
	| 'Google'
	| 'Apple'
	| 'Microsoft365'
	| 'Outlook'
	| 'Yahoo'
;

const setQueryArgs = (appt: Appointment | undefined) => {
	if (!appt) {
		return;
	}
	return {
		text: 'DEXA scan at ' + appt?.details.location.details.name,
		details: 'Dont forget to complete an intake form at https://intakes.bodyspec.com/dxa !',
		start: new Date(appt.details.start.unix * 1000),
		timezone: appt?.details.start.timezone,
		location: appt?.details.location.details.address.addr1 + ' '
			+ appt?.details.location.details.address.addr2,
		icsCalendar: appt.details.icsCalendar,
		durationMinutes: 12,
	};
};

const createApptString = (appt: Appointment | undefined, type: Calendar) => {
	if (!appt) {
		return;
	}

	const queryArgs = setQueryArgs(appt);
	if (!queryArgs) {
		return;
	}

	// Google calendar supports timezone info that the function does not support,
	// so we supply it here and append it to the generated string
	const googleTimezone = '&ctz=' + queryArgs.timezone;

	switch(type) {
	case 'Google': {
		const googleURL= google({
			title: queryArgs.text,
			description: queryArgs.details,
			start: queryArgs.start,
			duration: [queryArgs.durationMinutes, 'minutes'],
			location: queryArgs.location,
		}) + googleTimezone;
		window.open(googleURL, '_blank');
		break;
	}
	case 'Microsoft365': {
		const microoft365URL= office365({
			title: queryArgs.text.replace('&', 'and'),
			description: queryArgs.details,
			start: queryArgs.start,
			duration: [queryArgs.durationMinutes, 'minutes'],
			location: queryArgs.location,
		});
		window.open(microoft365URL, '_blank');
		break;
	}
	case 'Outlook': {
		const outlookURL= outlook({
			title: queryArgs.text.replace('&', 'and'),
			description: queryArgs.details,
			start: queryArgs.start,
			duration: [queryArgs.durationMinutes, 'minutes'],
			location: queryArgs.location,
		});
		window.open(outlookURL, '_blank');
		break;
	}
	case 'Yahoo': {
		const yahooURL= yahoo({
			title: queryArgs.text,
			description: queryArgs.details,
			start: queryArgs.start,
			duration: [queryArgs.durationMinutes, 'minutes'],
			location: queryArgs.location,
		});
		window.open(yahooURL, '_blank');
		break;
	}
	};
};

const icsFileHandler = (appt: Appointment | undefined, ref: React.RefObject<HTMLAnchorElement>) => {
	if (!appt) {
		return;
	}
	// In Safari, open link to file to allow browser to add to apple calendar
	if (isSafari) {
		const queryArgs = setQueryArgs(appt);
		if (!queryArgs) {
			return;
		}
		const appleURL= ics({
			title: queryArgs.text,
			description: queryArgs.details,
			start: queryArgs.start,
			duration: [queryArgs.durationMinutes, 'minutes'],
			location: queryArgs.location,
		});
		window.open(appleURL, '_blank');
		return;
	}

	// For other browsers, download ics file directly
	const file = new File(
		[appt.details.icsCalendar],
		'bodyspec-appointment.ics',
		{
			type: 'text/calendar,application/ics',
		},
	);

	// If download link exists, init & click
	if(ref.current) {
		const url = URL.createObjectURL(file);
		ref.current.href = url;
		ref.current.download = file.name;
		ref.current.click();
		URL.revokeObjectURL(url);
	}
	return;

};

export type calendarOption = {
	text: string
	icon: React.ReactNode
	onClick: (appt: Appointment | undefined, ref: React.RefObject<HTMLAnchorElement>) => void
}

export const calendarOptions: calendarOption[] = [
	{
		text: 'Google',
		icon: <Google />,
		onClick: (appt) => createApptString(appt, 'Google'),
	},
	{
		text: 'Apple',
		icon: <Apple />,
		onClick: (appt, ref) => icsFileHandler(appt, ref),
		// Special case for safari + apple calendar, will link to the ics file on iOS safari which
		// should prompt the user to add it to their calendar, other browsers will download the ics file
	},
	{
		text: 'Microsoft 365',
		icon: <Microsoft365 />,
		onClick: (appt) => createApptString(appt, 'Microsoft365'),
	},
	{
		text: 'Outlook',
		icon: <Outlook />,
		onClick: (appt) => createApptString(appt, 'Outlook'),
	},
	{
		text: 'Yahoo',
		icon: <Yahoo />,
		onClick: (appt) => createApptString(appt, 'Yahoo'),
	},
];
